import React, { useMemo, useState } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { Container, Spinner } from 'react-bootstrap';
import EntitySidebar from './EntitySidebar';
import Header from '@core/components/Header';
import AppFooter from '@core/components/AppFooter';
import '@features/dashboard/pages/dashboard.css';
import { useEntityContext, usePermissions } from '../hooks/usePermissions';
import { getApiScopeFromValue } from '@features/invitations/utils/getScopeFromParam';
import { ACTION_CODES, getScopedActionCode, MenuItemConfig } from '../types/actionCodes';
import {
  FiHome, FiFileText, FiUsers, FiUser,
  FiSettings, FiTarget, FiTag,
  FiMapPin, FiMail, FiBriefcase, FiLayers,
  FiActivity, FiSend, FiDollarSign
} from 'react-icons/fi';
import { getWorkflowStatusLabel } from '@core/reports/utils/reportWorkflow';

// `area` is the URL segment for the reports area, e.g. "department-reports"
// or "leadership-reports/pastor" — lets the same status children serve both.
function buildDepartmentReportMenuChildren(basePath: string, idPrefix: string, area: string = 'department-reports'): MenuItemConfig[] {
  return [
    {
      id: `${idPrefix}-all`,
      label: 'All',
      path: `${basePath}/${area}/all`,
    },
    {
      id: `${idPrefix}-draft`,
      label: getWorkflowStatusLabel('DRAFT'),
      path: `${basePath}/${area}/draft`,
    },
    {
      id: `${idPrefix}-submitted`,
      label: getWorkflowStatusLabel('SUBMITTED'),
      path: `${basePath}/${area}/submitted`,
    },
    {
      id: `${idPrefix}-needs-attention`,
      label: getWorkflowStatusLabel('NEEDS_ATTENTION'),
      path: `${basePath}/${area}/needs-attention`,
    },
    {
      id: `${idPrefix}-awaiting-approval`,
      label: getWorkflowStatusLabel('AWAITING_APPROVAL'),
      path: `${basePath}/${area}/awaiting-approval`,
    },
    {
      id: `${idPrefix}-approved`,
      label: getWorkflowStatusLabel('APPROVED'),
      path: `${basePath}/${area}/approved`,
    },
  ];
}

const EntityLayout: React.FC = () => {
  const { context, accessStatus } = useEntityContext();

  // Define all possible menu items with their required action codes
  const allMenuItems = useMemo(() => {
    if (!context) return [];

    const scope = context.scope;
    const basePath = `/${getApiScopeFromValue(scope)}/${context.scopeId}`;
    const departmentReportChildren = buildDepartmentReportMenuChildren(basePath, 'dept-reports');
    const pastorLeadershipChildren = buildDepartmentReportMenuChildren(basePath, 'leadership-pastor', 'leadership-reports/pastor');
    const financeStatsLeadershipChildren = buildDepartmentReportMenuChildren(basePath, 'leadership-finance', 'leadership-reports/finance-statistics');

    // --- 1. Dashboard (always visible) ---
    const items: MenuItemConfig[] = [
      {
        id: 'dashboard',
        label: 'Dashboard',
        path: `${basePath}/dashboard`,
        icon: <FiHome />,
      },

      // --- 2. Activity Submission (all scopes: New, All, Draft, Submitted, Needs Attention) ---
      {
        id: 'activity-submission',
        label: 'Activity Submission',
        icon: <FiSend />,
        children: [
          {
            id: 'activity-submission-new',
            label: 'New',
            path: `${basePath}/reports/submit`,
            actionCode: getScopedActionCode(scope, 'SAVE_ACTIVITY')
          },
          {
            id: 'activity-submission-all',
            label: 'All',
            path: `${basePath}/activity-submissions/all`,
          },
          {
            id: 'activity-submission-draft',
            label: getWorkflowStatusLabel('DRAFT'),
            path: `${basePath}/activity-submissions/draft`,
          },
          {
            id: 'activity-submission-submitted',
            label: getWorkflowStatusLabel('SUBMITTED'),
            path: `${basePath}/activity-submissions/submitted`,
          },
          {
            id: 'activity-submission-needs-attention',
            label: getWorkflowStatusLabel('NEEDS_ATTENTION'),
            path: `${basePath}/activity-submissions/needs-attention`,
          },
        ],
      },

      // --- 3. Department Reports (All + status filters) ---
      {
        id: 'department-reports',
        label: 'Department Reports',
        icon: <FiFileText />,
        children: departmentReportChildren,
      },
      {
        id: 'leadership-reports',
        label: 'Leadership Reports',
        icon: <FiFileText />,
        children: [
          {
            id: 'leadership-pastor',
            label: 'Pastor',
            icon: <FiUser />,
            leadershipKey: 'pastor',
            children: pastorLeadershipChildren,
          },
          {
            id: 'leadership-finance',
            label: 'Finance & Statistics',
            icon: <FiDollarSign />,
            leadershipKey: 'finance_statistics',
            children: financeStatsLeadershipChildren,
          },
          // District Overseer leadership type slots in here later.
        ],
      },

      // --- 4. Admin Config ---
      {
        id: 'admin',
        label: 'Admin',
        icon: <FiUsers />,
        children: [
          {
            id: 'users',
            label: 'Users',
            path: `${basePath}/users`,
            icon: <FiUsers />,
            actionCode: getScopedActionCode(scope, 'VIEW_USERS')
          },
          {
            id: 'user-role-assignments',
            label: 'User role assignments',
            path: `${basePath}/user-role-assignments`,
            icon: <FiUser />,
            actionCode: getScopedActionCode(scope, 'VIEW_USERS')
          },
          {
            id: 'invitations',
            label: 'Invitations',
            path: `${basePath}/invites`,
            icon: <FiMail />,
            actionCode: getScopedActionCode(scope, 'VIEW_INVITES')
          },
          // Assigned Templates (all scopes)
          {
            id: 'assigned-templates',
            label: scope === 'NATION' ? 'Nation Templates' : scope === 'DISTRICT' ? 'District Templates' : 'Church Templates',
            path: `${basePath}/templates`,
            icon: <FiBriefcase />,
            actionCode: ACTION_CODES.VIEW_DEPARTMENT_LIST
          },
          // District: Churches
          ...(scope === 'DISTRICT' ? [{
            id: 'churches',
            label: 'Churches',
            path: `${basePath}/churches`,
            icon: <FiHome />,
            actionCode: ACTION_CODES.VIEW_CHURCH_LIST
          }] : []),
          // Nation: Districts + Churches + Departments
          ...(scope === 'NATION' ? [
            {
              id: 'districts',
              label: 'Districts',
              path: `${basePath}/districts`,
              icon: <FiMapPin />,
              actionCode: ACTION_CODES.VIEW_DISTRICT_LIST
            },
            {
              id: 'churches',
              label: 'Churches',
              path: `${basePath}/churches`,
              icon: <FiHome />,
              actionCode: ACTION_CODES.VIEW_CHURCH_LIST
            },
            {
              id: 'departments',
              label: 'Departments',
              path: `${basePath}/departments`,
              icon: <FiBriefcase />,
              actionCode: ACTION_CODES.VIEW_DEPARTMENT_LIST
            },
          ] : []),
          // Nation-only config items
          ...(scope === 'NATION' ? [
            {
              id: 'activities',
              label: 'Activities',
              path: `${basePath}/activities`,
              icon: <FiActivity />,
              actionCode: ACTION_CODES.CREATE_ACTIVITY,
            },
            {
              id: 'kpi',
              label: 'KPI Management',
              path: `${basePath}/kpi`,
              icon: <FiTarget />,
              actionCode: ACTION_CODES.CREATE_KPI
            },
            {
              id: 'tags',
              label: 'Report Tags',
              path: `${basePath}/tags`,
              icon: <FiTag />,
              actionCode: ACTION_CODES.CREATE_REPORT_TAG
            },
            {
              id: 'subgroups',
              label: 'Report Sub Groups',
              path: `${basePath}/subgroups`,
              icon: <FiLayers />,
              actionCode: ACTION_CODES.CREATE_REPORT_SUB_GROUP,
            },
          ] : []),
        ]
      },

      // --- 5. Finances (NATION only) ---
      ...(scope === 'NATION' ? [{
        id: 'finances',
        label: 'Finances',
        icon: <FiDollarSign />,
        children: [
          {
            id: 'finance-categories',
            label: 'Categories',
            path: `${basePath}/finance/categories`,
            actionCode: ACTION_CODES.CREATE_CHURCH_FINANCIAL_CATEGORY,
          },
          {
            id: 'finance-allocation-rules',
            label: 'Allocation Rules',
            path: `${basePath}/finance/allocation-rules`,
            actionCode: ACTION_CODES.CREATE_FINANCE_ALLOCATION_RULE,
          },
        ],
      }] : []),

      // --- 6. Settings (always visible) ---
      {
        id: 'settings',
        label: 'Settings',
        path: `${basePath}/settings`,
        icon: <FiSettings />,
      },
    ];

    return items;
  }, [context]);

  // Get all action codes from menu items to check in bulk
  const requiredActionCodes = useMemo(() => {
    const codes: string[] = [];

    const extractCodes = (items: MenuItemConfig[]) => {
      items.forEach(item => {
        if (item.actionCode) {
          codes.push(item.actionCode);
        }
        if (item.children) {
          extractCodes(item.children);
        }
      });
    };

    extractCodes(allMenuItems);
    return codes;
  }, [allMenuItems]);

  // Check permissions
  const { permittedActions, leadership, loading } = usePermissions({
    scopeType: context?.scope,
    scopeId: context?.scopeId,
    actionCodes: requiredActionCodes,
    includeLeadership: true,
  });

  // Filter menu items based on permissions once the access check has settled.
  const visibleMenuItems = useMemo(() => {
    if (loading) {
      return [];
    }

    const filterItems = (items: MenuItemConfig[]): MenuItemConfig[] => {
      return items
        .filter(item => {
          if (item.id === 'leadership-reports' && leadership?.show_section !== true) {
            return false;
          }
          if (item.leadershipKey) {
            return leadership?.[item.leadershipKey] === true;
          }
          if (item.actionCode) {
            return permittedActions.includes(item.actionCode);
          }
          if (item.children) {
            const visibleChildren = filterItems(item.children);
            return visibleChildren.length > 0;
          }
          return true;
        })
        .map(item => {
          // Recursively filter children
          if (item.children) {
            return {
              ...item,
              children: filterItems(item.children)
            };
          }
          return item;
        });
    };

    return filterItems(allMenuItems);
  }, [allMenuItems, permittedActions, leadership, loading]);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isFullWidthReportPage =
    location.pathname.includes('/reports/view') ||
    (location.pathname.includes('/leadership-reports/') &&
      location.pathname.includes('/view'));

  // Verifying access — show a loading spinner
  if (accessStatus === 'loading') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  // Access denied — redirect to authenticated home
  if (accessStatus === 'denied') {
    return <Navigate to="/entities" replace state={{ accessDenied: true }} />;
  }

  // If no context at all (no URL match, no sessionStorage), render content without sidebar
  if (!context) {
    return (
      <div className="ntcog-app-shell">
        <div className="ntcog-app-shell__main flex-grow-1 d-flex flex-column">
          <Header title={`{userName}`} subtitle="" />
          <Container
            fluid
            className="p-0 flex-grow-1"
            style={{ overflowX: 'auto', overflowY: 'auto', minHeight: 0 }}
          >
            <Outlet />
          </Container>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex ntcog-app-shell ntcog-app-shell--with-sidebar">
      {/* Backdrop when entity sidebar is open on mobile */}
      {mobileMenuOpen && (
        <div
          className="entity-sidebar-backdrop d-lg-none"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden
        />
      )}
      <div
        className={`entity-sidebar-wrap ${mobileMenuOpen ? 'entity-sidebar-wrap--open' : ''}`}
        style={{ flexShrink: 0 }}
      >
        <EntitySidebar
          menuItems={visibleMenuItems}
          loading={loading}
          entityInfo={{
            scope: context.scope,
            scopeId: context.scopeId,
            scopeName: context.scopeName
          }}
          onCloseMobileMenu={() => setMobileMenuOpen(false)}
        />
      </div>
      <div
        className="ntcog-app-shell__main flex-grow-1 d-flex flex-column"
        style={{ width: 0 }}
      >
        <Header
          title=""
          subtitle=""
          entityScope={context.scope as 'NATION' | 'DISTRICT' | 'CHURCH'}
          entityName={context.scopeName}
          showMobileMenuButton={true}
          onMobileMenuToggle={() => setMobileMenuOpen(prev => !prev)}
        />
        <div className="ntcog-app-shell__content d-flex flex-column">
          {/* Full width for report data pages, 80% for list and other pages */}
          <main id="main" className={`ntcog-page-content ${isFullWidthReportPage ? 'ntcog-page-content--full-width' : ''}`} tabIndex={-1}>
            <Outlet />
          </main>
          <AppFooter />
        </div>
      </div>
    </div>
  );
};

export default EntityLayout;
